import React, { useState } from 'react'
import { Eye, EyeOff, Lock, ShieldCheck } from 'lucide-react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import type { MfaChallenge, MfaMethod } from '../services/authService'
import SiniBrand from './SiniBrand'
import { AuthError, MfaEnrollmentForm, RecoveryCodesPanel } from './AuthSecuritySteps'

type Stage = 'password' | 'enrollment' | 'recovery'

const InvitationAcceptancePage: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { acceptInvitation, confirmMfaEnrollment, sendMfaEnrollmentEmail } = useAuth()
  const [stage, setStage] = useState<Stage>('password')
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null)
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const passwordStrong = password.length >= 12 &&
    /[a-z]/.test(password) && /[A-Z]/.test(password) &&
    /\d/.test(password) && /[^A-Za-z0-9]/.test(password)

  if (!token) return <Navigate to="/login" replace />

  const accept = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!passwordStrong) return setError(t('invite_password_policy'))
    if (password !== confirmation) return setError(t('passwords_do_not_match'))
    setBusy(true)
    setError(null)
    try {
      const result = await acceptInvitation(token, password)
      if (result.status === 'error') return setError(result.error)
      if (result.status !== 'mfa_required' || !result.challenge.enrollmentRequired) {
        return setError(t('invite_enrollment_required'))
      }
      setChallenge(result.challenge)
      setStage('enrollment')
      setPassword('')
      setConfirmation('')
    } finally {
      setBusy(false)
    }
  }

  const confirmEnrollment = async (method: Exclude<MfaMethod, 'recovery'>, code: string) => {
    if (!challenge) return
    setBusy(true)
    setError(null)
    try {
      const result = await confirmMfaEnrollment(challenge.challengeToken, method, code)
      if (result.status === 'error') return setError(result.error)
      if (result.status !== 'authenticated' || !result.recoveryCodes?.length) {
        return setError(t('mfa_recovery_missing'))
      }
      setRecoveryCodes(result.recoveryCodes)
      setStage('recovery')
    } finally {
      setBusy(false)
    }
  }

  const sendEnrollmentEmail = async () => {
    if (!challenge) return false
    const result = await sendMfaEnrollmentEmail(challenge.challengeToken)
    if (!result.success) setError(result.error || t('login_error'))
    return result.success
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 px-4 py-10">
      <div className="mx-auto w-full max-w-lg">
        <div className="mb-6 flex justify-center"><div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm"><SiniBrand /></div></div>
        <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-xl sm:p-9">
          {stage === 'password' && (
            <form onSubmit={accept} className="space-y-5">
              <div className="text-center">
                <ShieldCheck className="mx-auto text-blue-600" size={34} />
                <h1 className="mt-3 text-2xl font-bold text-slate-900">{t('invite_title')}</h1>
                <p className="mt-1 text-sm text-slate-500">{t('invite_description')}</p>
              </div>
              <AuthError message={error} />
              {[{
                label: t('new_password'), value: password, set: setPassword, autoComplete: 'new-password'
              }, {
                label: t('confirm_password'), value: confirmation, set: setConfirmation, autoComplete: 'new-password'
              }].map((field, index) => (
                <label key={index} className="block text-sm font-medium text-slate-700">{field.label}
                  <div className="relative mt-2">
                    <Lock className="absolute left-3.5 top-3 text-slate-400" size={18} />
                    <input type={showPassword ? 'text' : 'password'} value={field.value}
                      onChange={event => field.set(event.target.value)} autoComplete={field.autoComplete}
                      minLength={12} required className="w-full rounded-xl border border-slate-300 py-3 pl-11 pr-11 text-sm outline-none focus:border-blue-500" />
                    <button type="button" onClick={() => setShowPassword(value => !value)}
                      className="absolute right-3.5 top-3 text-slate-400">
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </label>
              ))}
              <p className="text-xs text-slate-500">{t('invite_password_hint')}</p>
              <button disabled={busy || !passwordStrong || password !== confirmation}
                className="w-full rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50">
                {busy ? t('loading') : t('invite_accept')}
              </button>
            </form>
          )}
          {stage === 'enrollment' && challenge && <MfaEnrollmentForm challenge={challenge} busy={busy} error={error}
            onConfirm={confirmEnrollment} onSendEmail={sendEnrollmentEmail} />}
          {stage === 'recovery' && <RecoveryCodesPanel codes={recoveryCodes} onContinue={() => navigate('/dashboard')} />}
        </div>
        <div className="mt-5 text-center text-xs text-slate-400">fleet.sinigroupe.com</div>
      </div>
    </div>
  )
}

export default InvitationAcceptancePage
